'use client'

import { Button, Cell, Dialog, Image, Spacer, Text } from '@vinted/web-ui'

import { useIsDarkMode } from '@marketplace-web/dark-mode/dark-mode-feature'
import { useTranslate } from '@marketplace-web/i18n/i18n-feature'
import { ScrollableArea } from '@marketplace-web/common-components/scrollable-area-ui'
import { useImageSrcSet } from '@marketplace-web/shared/assets'
import { useAbTest, useTrackAbTest } from '@marketplace-web/feature-flags/ab-tests-data'

type Props = {
  isOpen: boolean
  onSubmit: () => void
  onClose: () => void
}

const ConfirmDeliveredEverythingIsOkModal = ({ isOpen, onSubmit, onClose }: Props) => {
  const translate = useTranslate('conversation.confirm_delivered_everything_is_ok_modal')
  const isDarkMode = useIsDarkMode()

  const everythingIsOkImageChangeV2AbTest = useAbTest('everything_ok_image_change_v2')
  useTrackAbTest(everythingIsOkImageChangeV2AbTest, isOpen)
  const isEverythingOkImageChangeV2AbTestEnabled =
    everythingIsOkImageChangeV2AbTest?.variant === 'on'
  const imageBaseName = isEverythingOkImageChangeV2AbTestEnabled
    ? 'general-check-box-200'
    : 'general-check-200'

  const { src, srcSet } = useImageSrcSet({
    baseName: imageBaseName,
    darkMode: isDarkMode,
    assetBasePath: '/assets/complete-delivered-transaction',
  })

  const renderBody = () => {
    return (
      <Cell>
        <div className="u-text-center">
          <Image role="img" src={src} srcset={srcSet} />
        </div>
        <Text
          as="h1"
          width={Text.Width.Parent}
          alignment={Text.Alignment.Center}
          type={Text.Type.Heading}
          text={translate('title_complete_order')}
          html
        />
        <Spacer size={Spacer.Size.Large} />
        <Text as="span" text={translate('body_complete_order')} html />
      </Cell>
    )
  }

  const renderActions = () => {
    return (
      <Cell>
        <Button
          text={translate('actions.confirm_complete_order')}
          onClick={onSubmit}
          styling={Button.Styling.Filled}
          testId="confirm-everything-is-ok-modal--confirm-button"
        />
        <Spacer size={Spacer.Size.Medium} />
        <Button
          text={translate('actions.close')}
          onClick={onClose}
          testId="confirm-everything-is-ok-modal--close-button"
          styling={Button.Styling.Flat}
        />
      </Cell>
    )
  }

  return (
    <Dialog hasScrollableContent testId="confirm-everything-is-ok-modal" show={isOpen}>
      <ScrollableArea>
        {renderBody()}
        {renderActions()}
      </ScrollableArea>
    </Dialog>
  )
}

export default ConfirmDeliveredEverythingIsOkModal
